import { Suspense, useEffect, useMemo, useState } from 'react';
import './App.scss';
import { useLocation } from 'react-router-dom';
import { Container } from 'react-bootstrap';
import MainNavbar from '../../components/MainNavbar';
import useWindowSize from '../../helpers/hooks/useWindowSize';
import MainFooter from '../../components/MainFooter';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
	setLoggedInStatus,
	setTenancyStatus,
	setUserRole,
} from '../../redux/slicers/authenticationSlice';
import useGoogleTracking from '../../helpers/hooks/useGoogleTracking';
import permissions from '../../helpers/permissions';
import AppRoutes from '../../router/routes';
import { clientSiteDataReset, userLogout } from '../../helpers/deinitialization';
import { initializeData, initializeTenancy, initializeUser } from '../../helpers/initialization';

const App = () => {
	const dispatch = useAppDispatch();

	// TEMPORARY LOADING LOGIC
	const [loading, setLoading] = useState<boolean>(true);
	const [isLoginView, setIsLoginView] = useState<boolean>(true);
	const [isAdmin, setIsAdmin] = useState<boolean>(false);

	const loggedIn = useAppSelector((state) => state.authentication.loggedIn);
	const tenancyStatus = useAppSelector((state) => state.authentication.tenancyStatus);
	const withoutToken = useAppSelector((state) => state.authentication.withoutToken);
	const user = useAppSelector((state) => state.authentication.user);
	const userRole = useAppSelector((state) => state.authentication.userRole);
	const roles = useAppSelector((state) => state.reference.roles);
	const clientTotal = useAppSelector((state) => Object.values(state.tenancy.clients).length);
	const siteTotal = useAppSelector((state) => Object.values(state.tenancy.sites).length);
	const selectedSite = useAppSelector((state) => state.tenancy.selectedSite);
	const selectedRoom = useAppSelector((state) => state.room.selectedRoom);

	// Hook for checking users window size
	const windowSize = useWindowSize();
	const location = useLocation();

	const [isRoomView, setIsRoomView] = useState<boolean>(false);

	useGoogleTracking();

	const multipleTenancyStatus = useMemo(() => {
		if (loggedIn && !withoutToken) {
			return clientTotal > 1 && siteTotal > 1;
		}
		return false;
	}, [clientTotal, loggedIn, siteTotal, withoutToken]);

	useEffect(() => {
		(async () => {
			setLoading(true);
			await setup();
			setLoading(false);
		})();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (user && roles && selectedSite) {
			dispatch(setUserRole(permissions.checkUserRole(user, Object.values(roles), selectedSite.id)));
		} else {
			dispatch(setUserRole(undefined));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [roles, selectedSite, user]);

	useEffect(() => {
		setIsAdmin(permissions.hasPermission(userRole));
	}, [userRole]);

	useEffect(() => {
		const path = location.pathname.split('/');
		document.title = definePageTile(path);
		setIsRoomView(path[1] === 'room');
		setIsLoginView(path[1] === 'login');

		function definePageTile(title: string[]): string {
			switch (title[1]) {
				case 'login':
					return 'TempSense | Log in';
				case 'dashboard':
					return 'TempSense | Dashboard';
				case 'room':
					if (selectedRoom) {
						if (title[3]) {
							return `TempSense | ${selectedRoom.name} Graafik `;
						} else {
							return `TempSense | ${selectedRoom.name} `;
						}
					} else {
						return 'TempSense';
					}
				case 'analyze':
					return 'TempSense | Analüüs';
				case 'notification':
					return 'TempSense | Teavituste saajad';
				case 'info':
					return 'TempSense | Info';
				case 'report':
					return 'TempSense | Aruanne';
				default:
					return 'TempSense';
			}
		}
	}, [location, selectedRoom]);

	async function setup() {
		const user = await initializeUser();
		if (!user) {
			return;
		}
		const tenancyResult = await initializeTenancy();
		if (!tenancyResult) {
			return;
		}
		const currentClient = localStorage.getItem('clientId');
		const currentSite = localStorage.getItem('siteId');
		await dispatch(setLoggedInStatus(true));

		if (!currentClient || !currentSite) {
			await dispatch(setTenancyStatus(false));
			return;
		}

		const dataResult = await initializeData(Number(currentClient), Number(currentSite), true);
		if (dataResult) {
			await dispatch(setTenancyStatus(true));
		} else {
			userLogout(true);
		}
	}

	function changeClientSite() {
		clientSiteDataReset(true);
	}

	function logout() {
		userLogout(true);
	}

	return !loading ? (
		<div className={`d-flex flex-column min-vh-100 ${isLoginView && 'login-page'} `}>
			<Suspense fallback={<h5>Loading...</h5>}>
				<MainNavbar
					windowSize={windowSize}
					isLoggedIn={loggedIn}
					tenancyStatus={tenancyStatus}
					withoutToken={withoutToken}
					multipleTenancy={multipleTenancyStatus}
					emitLogout={logout}
					emitClientSiteChange={changeClientSite}
					isAdmin={isAdmin}
				/>
				<main className={`${!isLoginView ? 'mt-4' : 'my-auto'}  `}>
					<Container>
						<Suspense fallback={<h5>Loading...</h5>}>
							<AppRoutes />
						</Suspense>
					</Container>
				</main>
				<MainFooter isLoggedIn={!isLoginView} isRoomView={isRoomView} />
			</Suspense>
		</div>
	) : (
		<></>
	);
};

export default App;
